/**
 * @fileoverview Manages four-line expansion controls for live user-chat bubbles.
 * Measures rendered text, animates between explicit pixel heights, and remeasures
 * when a bubble's width changes. Read-only transcripts intentionally do not use it.
 *
 * @module utils/TruncationManager
 */

import { Tooltip } from './Tooltip.js';

const MAX_COLLAPSED_LINES = 4;
const OVERFLOW_TOLERANCE_PX = 1;
const WIDTH_CHANGE_TOLERANCE_PX = 0.5;
const states = new WeakMap();

/**
 * Controls expansion and collapse behavior for live user messages.
 *
 * @class TruncationManager
 */
export class TruncationManager {
  /**
   * Initialize expansion behavior for one live user-message element.
   *
   * @param {HTMLElement} messageElement - A `.message--user` wrapper
   * @returns {void}
   */
  static initializeTruncation(messageElement) {
    if (!messageElement?.classList.contains('message--user')) return;

    const contentDiv = messageElement.querySelector('.message__content');
    if (!contentDiv) return;

    const existingState = states.get(contentDiv);
    if (existingState) {
      this._scheduleMeasurement(contentDiv, existingState);
      return;
    }

    const textElement = contentDiv.querySelector(':scope > p');
    if (!textElement) {
      contentDiv.dataset.expandable = 'false';
      contentDiv.dataset.expanded = 'false';
      return;
    }

    const textClip = this._createTextClip(textElement);
    const { toggleButton, toggleShell, toggleFade } = this._createToggleControl(contentDiv);
    const state = {
      textElement,
      textClip,
      toggleButton,
      toggleShell,
      toggleFade,
      expanded: false,
      collapsedHeight: 0,
      fullHeight: 0,
      lastObservedWidth: null,
      animationFrame: null,
      resizeObserver: null,
      tooltipCleanup: null
    };

    states.set(contentDiv, state);
    state.tooltipCleanup = Tooltip.setup(toggleButton, {
      position: 'below',
      getText: () => state.expanded ? 'Collapse text' : 'Expand text'
    });
    toggleButton.addEventListener('click', () => {
      this.toggleTruncation(contentDiv, toggleButton);
    });
    contentDiv.addEventListener('click', event => {
      if (event.target.closest('.message__toggle')) return;
      if (contentDiv.dataset.expandable !== 'true' || state.expanded) return;
      if (window.getSelection()?.toString().length) return;
      this.toggleTruncation(contentDiv, toggleButton);
    });

    if (typeof ResizeObserver === 'function') {
      state.resizeObserver = new ResizeObserver(entries => {
        const width = entries[0]?.contentRect?.width;
        if (!Number.isFinite(width)) return;
        if (
          state.lastObservedWidth !== null
          && Math.abs(width - state.lastObservedWidth) <= WIDTH_CHANGE_TOLERANCE_PX
        ) {
          return;
        }

        state.lastObservedWidth = width;
        this._scheduleMeasurement(contentDiv, state);
      });
      state.resizeObserver.observe(contentDiv);
    }

    this._scheduleMeasurement(contentDiv, state);
  }

  /**
   * Toggle one initialized user message between four lines and its full height.
   *
   * @param {HTMLElement} contentDiv - The bubble's `.message__content` element
   * @param {HTMLButtonElement} toggleButton - The bubble's chevron button
   * @returns {void}
   */
  static toggleTruncation(contentDiv, toggleButton) {
    const state = states.get(contentDiv);
    if (!state || contentDiv.dataset.expandable !== 'true') return;

    state.expanded = !state.expanded;
    contentDiv.dataset.expanded = String(state.expanded);
    toggleButton.setAttribute('aria-expanded', String(state.expanded));
    toggleButton.setAttribute('aria-label', state.expanded ? 'Collapse message' : 'Expand message');
    contentDiv.style.setProperty(
      '--message-text-height',
      `${state.expanded ? state.fullHeight : state.collapsedHeight}px`
    );
  }

  /**
   * Remeasure every initialized live user message in a container.
   *
   * @param {HTMLElement} container - Container holding live chat messages
   * @returns {void}
   */
  static reinitializeAll(container) {
    if (!container) return;

    container.querySelectorAll('.message--user').forEach(message => {
      this.initializeTruncation(message);
    });
  }

  /**
   * Disconnect observers and cancel pending measurements before messages are removed.
   *
   * @param {HTMLElement} container - Container holding live chat messages
   * @returns {void}
   */
  static destroyAll(container) {
    if (!container) return;

    container.querySelectorAll('.message--user .message__content').forEach(contentDiv => {
      const state = states.get(contentDiv);
      if (!state) return;

      state.resizeObserver?.disconnect();
      state.tooltipCleanup?.();
      if (state.animationFrame !== null) cancelAnimationFrame(state.animationFrame);
      states.delete(contentDiv);
    });
  }

  /**
   * Build the fade, solid backing shell, and icon-only control anchored in the
   * bubble's final row. The square shell prevents text from showing through the
   * rounded button corners.
   *
   * @param {HTMLElement} contentDiv - The bubble content that owns the button
   * @returns {Object} Toggle control elements
   * @returns {HTMLButtonElement} returns.toggleButton - Chevron button
   * @returns {HTMLDivElement} returns.toggleShell - Solid bubble-colored backing
   * @returns {HTMLDivElement} returns.toggleFade - Adjacent left-side fade
   * @private
   */
  static _createToggleControl(contentDiv) {
    const fade = document.createElement('div');
    fade.className = 'message__toggle-fade';
    fade.hidden = true;
    fade.setAttribute('aria-hidden', 'true');

    const shell = document.createElement('div');
    shell.className = 'message__toggle-shell';
    shell.hidden = true;

    const button = document.createElement('button');
    button.className = 'message__toggle';
    button.type = 'button';
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-label', 'Expand message');
    button.innerHTML = `
      <svg class="message__toggle-icon" viewBox="0 0 16 16" aria-hidden="true">
        <path d="M4 6.25 8 10l4-3.75"></path>
      </svg>
    `;
    shell.appendChild(button);
    contentDiv.append(fade, shell);
    return { toggleButton: button, toggleShell: shell, toggleFade: fade };
  }

  /**
   * Wrap the paragraph in the element that owns clipping and height animation.
   * Keeping overflow off the bubble itself prevents the chevron from being cut off.
   *
   * @param {HTMLParagraphElement} textElement - User-message paragraph
   * @returns {HTMLDivElement} Text clipping wrapper
   * @private
   */
  static _createTextClip(textElement) {
    const textClip = document.createElement('div');
    textClip.className = 'message__text-clip';
    textElement.before(textClip);
    textClip.appendChild(textElement);
    return textClip;
  }

  /**
   * Coalesce layout measurements into the next animation frame.
   *
   * @param {HTMLElement} contentDiv - Bubble content to measure
   * @param {Object} state - Internal state associated with the bubble
   * @returns {void}
   * @private
   */
  static _scheduleMeasurement(contentDiv, state) {
    if (state.animationFrame !== null) cancelAnimationFrame(state.animationFrame);
    state.animationFrame = requestAnimationFrame(() => {
      state.animationFrame = null;
      this._measure(contentDiv, state);
    });
  }

  /**
   * Calculate the four-line and natural heights, then synchronize DOM state.
   * Only the text paragraph determines expandability; image attachments remain intact.
   *
   * @param {HTMLElement} contentDiv - Bubble content to measure
   * @param {Object} state - Internal state associated with the bubble
   * @returns {void}
   * @private
   */
  static _measure(contentDiv, state) {
    const computed = getComputedStyle(contentDiv);
    const lineHeight = this._resolveLineHeight(computed);
    const textHeight = state.textElement.scrollHeight;
    const collapsedTextHeight = lineHeight * MAX_COLLAPSED_LINES;
    const isExpandable = textHeight > collapsedTextHeight + OVERFLOW_TOLERANCE_PX;

    if (!isExpandable) {
      state.expanded = false;
      state.collapsedHeight = 0;
      state.fullHeight = 0;
      contentDiv.dataset.expandable = 'false';
      contentDiv.dataset.expanded = 'false';
      contentDiv.style.removeProperty('--message-text-height');
      state.toggleShell.hidden = true;
      state.toggleFade.hidden = true;
      state.toggleButton.setAttribute('aria-expanded', 'false');
      state.toggleButton.setAttribute('aria-label', 'Expand message');
      return;
    }

    state.collapsedHeight = Math.ceil(collapsedTextHeight);
    state.fullHeight = Math.ceil(textHeight);

    contentDiv.dataset.expandable = 'true';
    contentDiv.dataset.expanded = String(state.expanded);
    state.toggleShell.hidden = false;
    state.toggleFade.hidden = false;
    state.toggleButton.setAttribute('aria-expanded', String(state.expanded));
    state.toggleButton.setAttribute('aria-label', state.expanded ? 'Collapse message' : 'Expand message');
    contentDiv.style.setProperty(
      '--message-text-height',
      `${state.expanded ? state.fullHeight : state.collapsedHeight}px`
    );
  }

  /**
   * Resolve a numeric line height even when the computed value is `normal`.
   *
   * @param {CSSStyleDeclaration} computed - Computed bubble styles
   * @returns {number} Rendered line height in pixels
   * @private
   */
  static _resolveLineHeight(computed) {
    const lineHeight = parseFloat(computed.lineHeight);
    if (Number.isFinite(lineHeight)) return lineHeight;

    const fontSize = parseFloat(computed.fontSize);
    return (Number.isFinite(fontSize) ? fontSize : 16) * 1.5;
  }
}
